import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Snackbar,
  TablePagination,
  TextField,
} from '@material-ui/core';
import { ChangeEvent, useMemo, useState } from 'react';
import { SortedPatternMetadata } from '@internal/plugin-redis-analysis-common';
import { TablePaginationActions } from '../TablePaginationActions';
import { UnlinkConfirmationModal } from '../UnlinkConfirmationModal';
import {
  DEFAULT_PAGE,
  ROWS_PER_PAGE,
  SUCCESS_MESSAGE_DURATION,
  TABLE_HEAD_CELLS,
} from './constants';
import { Alert } from '@material-ui/lab';

interface RedisTableProps {
  data: SortedPatternMetadata[];
}

const useStyles = makeStyles(() => ({
  tableHead: {
    fontWeight: 'bold',
  },
}));

export const RedisTable = ({ data }: RedisTableProps) => {
  const classes = useStyles();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [searchText, setSearchText] = useState<string>('');
  const [pattern, setPattern] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleOpenModal = (selectedPattern: string) => {
    setPattern(selectedPattern);
    setModalOpen(true);
  };

  const handleCloseModal = (success: boolean) => {
    setPattern('');
    setModalOpen(false);
    setSnackbarOpen(success);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);
    setPage(DEFAULT_PAGE);
  };

  const filteredData = useMemo(
    () =>
      data?.filter(item =>
        item.pattern.toLowerCase().includes(searchText.toLowerCase()),
      ) ?? [],
    [data, searchText],
  );

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        autoHideDuration={SUCCESS_MESSAGE_DURATION}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={handleCloseSnackbar}>
          Unlink operation executed successfully
        </Alert>
      </Snackbar>
      <UnlinkConfirmationModal
        open={modalOpen}
        pattern={pattern}
        onClose={handleCloseModal}
      />
      <TextField
        style={{ maxWidth: 600 }}
        placeholder="Search"
        value={searchText}
        onChange={handleSearch}
      />
      <Paper>
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                {TABLE_HEAD_CELLS.map(cell => (
                  <TableCell
                    variant="head"
                    className={classes.tableHead}
                    style={{ width: cell.width }}
                  >
                    {cell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(row => (
                  <TableRow key={row.pattern}>
                    <TableCell>{row.pattern}</TableCell>
                    <TableCell>{row.size}</TableCell>
                    <TableCell>{row.count}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpenModal(row.pattern)}
                      >
                        UNLINK
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={event =>
            setRowsPerPage(Number(event.target.value))
          }
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
    </>
  );
};
